import { layers, namedFlavor } from "@protomaps/basemaps";
import type { LayerSpecification, StyleSpecification } from "maplibre-gl";

// Basemaps for the public demo, all served by /basemap from R2 (no Mapbox, nothing outside Cloudflare):
// - world.pmtiles: Protomaps, the planet up to zoom 8;
// - detail-pontenova.pmtiles: Protomaps, full detail around Ponte Nova, drawn from zoom 9;
// - s2-pontenova.pmtiles: Sentinel-2 cloudless 2016 imagery (EOX, CC BY 4.0), 10 m pixels, zoom 8-14.
const DETAIL_FROM_ZOOM = 9;
const OSM =
  '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>';
const S2 =
  '<a href="https://s2maps.eu">Sentinel-2 cloudless 2016</a> by EOX IT Services GmbH (contains modified Copernicus Sentinel data 2016), CC BY 4.0';

export type MapKind = "bairros" | "points";
// Layers the app inserts its own data before: everything above the base, below the labels.
export const LABELS_ANCHOR = "labels-anchor";

const prefix = (list: LayerSpecification[], id: string) =>
  list.map((l) => ({ ...l, id: `${id}-${l.id}` }) as LayerSpecification);

export function basemapStyle(kind: MapKind, origin: string): StyleSpecification {
  const flavor = namedFlavor("dark");
  const world = prefix(layers("world", flavor, { lang: "pt" }) as LayerSpecification[], "world");
  const detail = prefix(layers("detail", flavor, { lang: "pt" }) as LayerSpecification[], "detail")
    .filter((l) => l.type !== "background")
    .map((l) => ({ ...l, minzoom: DETAIL_FROM_ZOOM }) as LayerSpecification);

  const isLabel = (l: LayerSpecification) => l.type === "symbol";
  const worldBase = world.filter((l) => !isLabel(l));
  const worldLabels = world
    .filter(isLabel)
    .map((l) => ({ ...l, maxzoom: DETAIL_FROM_ZOOM }) as LayerSpecification);
  const detailBase = detail.filter((l) => !isLabel(l));
  const detailLabels = detail.filter(isLabel);

  // Over the imagery only roads and boundaries are drawn, no land, water or buildings.
  const base =
    kind === "bairros"
      ? [...worldBase, ...detailBase]
      : [
          ...worldBase,
          { id: "s2", type: "raster", source: "s2" } as LayerSpecification,
          ...detailBase.filter((l) => l.type === "line"),
        ];

  const anchor: LayerSpecification = {
    id: LABELS_ANCHOR,
    type: "fill",
    source: "anchors",
    paint: { "fill-opacity": 0 },
  };

  return {
    version: 8,
    glyphs: `${origin}/basemap/fonts/{fontstack}/{range}.pbf`,
    sprite: `${origin}/basemap/sprites/v4/dark`,
    sources: {
      world: { type: "vector", url: `pmtiles://${origin}/basemap/world.pmtiles`, attribution: OSM },
      detail: { type: "vector", url: `pmtiles://${origin}/basemap/detail-pontenova.pmtiles` },
      ...(kind === "points"
        ? { s2: { type: "raster", url: `pmtiles://${origin}/basemap/s2-pontenova.pmtiles`, tileSize: 256, attribution: S2 } }
        : {}),
      anchors: { type: "geojson", data: { type: "FeatureCollection", features: [] } },
    },
    layers: [...base, anchor, ...worldLabels, ...detailLabels],
  };
}
